import AddIcon from '@mui/icons-material/Add';
import { Button } from '@mui/material';
import { cloneDeep, concat, get, set } from 'lodash';
import React, { FC, useEffect, useState } from 'react';
import CreateForm from 'src/components/ui-component/form-component/create-form/CreateForm';
import MUIAccordionComponent from 'src/components/ui-component/mui-accordion/MUIAccordionComponent';
import MUIAlert from 'src/components/ui-component/mui-alert/MUIAlert';
import { useApplication } from 'src/context/ApplicationContext';
import { useAuthentication } from 'src/context/AuthContext';
import DeleteRecord from 'src/lib/api/DeleteRecord';
import GetRecords from 'src/lib/api/GetRecords';
import { APIS } from 'src/lib/constants/apis';
import { withKostenkategorieEinsatzbereichFilter } from 'src/utils/einsatzbereichHelper';
import {
	ALS_ERLEDIGT_MARKIEREN_LABEL,
	ERLAUTERUNG_IN_KOSTEN_TITLE,
	KOSTEN_ARTEN,
	KOSTEN_POSITION,
	KOSTENTRAEGER,
	PARTNER_DATEN_NICHT_ERFASST,
	PLATZHALTER_TEXT,
} from 'src/lib/constants/constant';
import { FormStrucItem } from 'src/lib/types/types';
import { ForceTrue, GetGesamtAusgabeGEGesamtEinnahme } from 'src/lib/validation/ValidationTypes';
import { checkIfSpecificKostenPositionHasComment } from 'src/pages/private/online-antrag/application-helper-function/PruefAssistenzHelperFunktion';
import { getFormStrucComponentTable, getFormStrucWithoutComponentTable } from 'src/utils/formStruc';
import { addIndexesToPath, checkReadOnlyInUeberarbeitung, getOptionsFromRes, isTrue, removeDataKeys, removeNullDeep } from 'src/utils/helper';
import PruefAssistenz from '../../../../antrag-components/PruefAssistenz';
import ApplicationHeading from '../../../../application-heading/ApplicationHeading';
import { getBasisDataKostenKategorie, getKostenErlauterung } from '../../../../application-helper-function/AppHelperfunctions';
import EinnahmenHinweisGlobal from '../../../global-structure/einnahmen-hinweis/content/EinnahmenHinweisGlobal';
import { transformData } from '../../../global-structure/finanzierung-plan-antrag/content/FinanzierungsplanGlobal';

// import schemas
import AntragSchema from 'src/lib/paths/antrag_FULL_Paths.json';
import KostenPositionSchema from 'src/lib/paths/kostenposition_FULL_Paths.json';
const P = AntragSchema.attributes;
const KP_P = KostenPositionSchema.attributes;

const EinnahmeAntragEbusse: FC<{ hasComment: boolean }> = ({ hasComment }) => {
	const { authenticatedData } = useAuthentication();
	const { application, setApplicationProperty, saveApplication, readOnly } = useApplication();
	const [allKostenKategories, setAllKostenKategories] = useState([]);

	const updatedReadOnly = checkReadOnlyInUeberarbeitung(application, readOnly, hasComment);

	const unternehmenId = get(authenticatedData, 'organisationId');

	const handleAddEigenmittel = async () => {
		// autofold/fold_next
		let appCopy = cloneDeep(application);
		const data = getBasisDataKostenKategorie(
			appCopy,
			allKostenKategories, // All kostenkategorie for Sachkosten, to find default Kostenkategorie id
			KOSTENTRAEGER.Antragsteller,
			unternehmenId,
			KOSTEN_POSITION.Einnahme,
			KOSTEN_ARTEN.Eigenmittel,
		);
		set(
			appCopy,
			addIndexesToPath(
				P.Foerderantrag_Kostenpositions.path,
				[`${get(appCopy, P.Foerderantrag_Kostenpositions.path, []).length}`],
				['Foerderantrag_Kostenpositions'],
			),
			{ attributes: data },
		);
		saveApplication(false, appCopy);
	};
	const handleDeleteEigenmittel = async (id: number) => {
		// autofold/fold_next
		try {
			let appcopy = cloneDeep(application);
			const allKostenposition = get(appcopy, P.Foerderantrag_Kostenpositions.path, []);
			set(
				appcopy,
				P.Foerderantrag_Kostenpositions.path,
				allKostenposition.filter((item: any) => item.id !== id),
			);
			let res = await DeleteRecord(APIS.Kostenposition, id);
			if (res) {
				saveApplication(false, appcopy);
			}
		} catch (err: any) {
			console.error('ERROR: Deleting Personalkosten', err);
		}
	};

	// Fetch all Kostenkategories
	useEffect(() => {
		// autofold/fold_next
		const fetch = async () => {
			try {
				const filter = withKostenkategorieEinsatzbereichFilter({
					Allgemein_Kostenposition: KOSTEN_POSITION.Einnahme,
					Allgemein_Kostenart_Finanzierung: KOSTEN_ARTEN.Eigenmittel,
					Allgemein_Antragsformular: { $contains: get(application, P.Allgemein_Antragsformular.path) },
					Allgemein_Aktiv: { $eq: true },
				}, APIS.Antrag);
				let res = await GetRecords(APIS.Kostenkategorie, [], filter);
				setAllKostenKategories(removeNullDeep(removeDataKeys(get(res, 'data', []))));
			} catch (err: any) {
				console.error('ERROR: Fetching Kostenkategories ', err);
			} finally {
			}
		};
		fetch();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<>
			<ApplicationHeading title='Eigenmittel' description={PLATZHALTER_TEXT} />
			<EinnahmenHinweisGlobal kostentraeger={KOSTENTRAEGER.Antragsteller} unternehmenId={unternehmenId} />
			{/* eslint-disable-next-line array-callback-return*/}
			{get(application, P.Foerderantrag_Kostenpositions.path, []).map((eachKosten: any, i: number) => {
				if (
					get(eachKosten, KP_P.Allgemein_Kostenposition.path) === KOSTEN_POSITION.Einnahme &&
					get(eachKosten, KP_P.Allgemein_Kostenart_Finanzierung.path) === KOSTEN_ARTEN.Eigenmittel &&
					get(eachKosten, `${KP_P.Unternehmen.path}.id`) === unternehmenId
				) {
					return (
						<MUIAccordionComponent
							key={i}
							summaryTitle={`Eigenmittel  ${get(eachKosten, KP_P.Allgemein_Bezeichnung.path, '')}`}
							onDelete={() => handleDeleteEigenmittel(get(eachKosten, 'id'))}
							showDeleteButton={!updatedReadOnly}
							hasComment={checkIfSpecificKostenPositionHasComment(readOnly, eachKosten)}
						>
							<PruefAssistenz
								statusPath={addIndexesToPath(
									P.Foerderantrag_Kostenpositions.schema.attributes.Pruefung_Pruefstatus.path,
									[`${i}`],
									['Foerderantrag_Kostenpositions'],
								)}
								hinweisPath={addIndexesToPath(
									P.Foerderantrag_Kostenpositions.schema.attributes.Pruefung_Hinweis_Fuer_Antragsteller.path,
									[`${i}`],
									['Foerderantrag_Kostenpositions'],
								)}
								ueberarbeitetAmPath={addIndexesToPath(
									P.Foerderantrag_Kostenpositions.schema.attributes.Pruefung_Ueberarbeitet_Am.path,
									[`${i}`],
									['Foerderantrag_Kostenpositions'],
								)}
								erledigtPath={addIndexesToPath(
									P.Foerderantrag_Kostenpositions.schema.attributes.Pruefung_Erledigt.path,
									[`${i}`],
									['Foerderantrag_Kostenpositions'],
								)}
								formStruc={mapKosten(PruefAssistenzStruc(), i)}
							/>
							<MUIAlert
								title={ERLAUTERUNG_IN_KOSTEN_TITLE}
								message={getKostenErlauterung(allKostenKategories, get(eachKosten, KP_P.Allgemein_Kostenkategorie.path))}
							/>
							<CreateForm
								formStruc={mapKosten(formStrucAllgemein(allKostenKategories), i)}
								formValues={application}
								updateFormValuesFunc={setApplicationProperty}
								readOnly={updatedReadOnly}
								api={APIS.Kostenposition}
							/>

							<CreateForm
								formStruc={mapKosten(formStrucKostenProJahr(application, i), i)}
								formValues={application}
								updateFormValuesFunc={setApplicationProperty}
								readOnly={updatedReadOnly}
								api={APIS.Kostenposition}
							/>
						</MUIAccordionComponent>
					);
				}
			})}
			{unternehmenId ?
				<Button sx={{ mt: '2rem', maxWidth: 'fit-content' }} endIcon={<AddIcon />} onClick={handleAddEigenmittel} disabled={updatedReadOnly}>
					Eigenmittel hinzufügen
				</Button>
			:	<MUIAlert message={PARTNER_DATEN_NICHT_ERFASST} />}
		</>
	);
};

export default EinnahmeAntragEbusse;

const PruefAssistenzStruc = () => {
	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					...P.Foerderantrag_Kostenpositions.schema.attributes.Pruefung_Erledigt,
					label: ALS_ERLEDIGT_MARKIEREN_LABEL,
					validation: [{ validateFunction: ForceTrue }],
				},
			],
		},
	];
	return struc;
};
const formStrucAllgemein = (allKostenKategories?: any) => {
	const kostenKategoriesOptions = allKostenKategories || [];
	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					...P.Foerderantrag_Kostenpositions.schema.attributes.Allgemein_Kostenkategorie,
					type: 'singleSelect',
					options: getOptionsFromRes(kostenKategoriesOptions, 'Allgemein_Bezeichnung'),
				},
				P.Foerderantrag_Kostenpositions.schema.attributes.Allgemein_Bezeichnung,
			],
			userProps: { columnToDivide: 2 },
		},
	];
	return struc;
};
const formStrucKostenProJahr = (application: any, i: number) => {
	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					...P.Foerderantrag_Kostenpositions.schema.attributes.Allgemein_Kosten_Pro_Jahr,
					type: 'componentTable',
					maxLevel: get(application, P.Allgemein_Projektdauerin_Jahren.path),
					disabledColumns: ['Zelle_1'],
					fieldProps: { Zelle_2: { format: 'currency' } },
					hideDeleteButton: true,
					sumRow: [
						{
							id: 'pinnedRow',
							Zelle_1: 'Gesamt',
							Zelle_2: get(
								application,
								addIndexesToPath(
									P.Foerderantrag_Kostenpositions.schema.attributes.Allgemein_Geplante_Gesamtsumme.path,
									[`${i}`],
									['Foerderantrag_Kostenpositions'],
								),
							),
						},
					],
				},
			],
			userProps: { columnToDivide: 2 },
		},
	];
	return struc;
};

const formStrucEigenmittel = (application: any, kostenKategorie: any, gesamtEinnahme: number, gesamtAusgabe: number): FormStrucItem[] => [
	{
		fields: [
			{
				...P.Foerderantrag_Kostenpositions.schema.attributes.Allgemein_Kostenkategorie,
				type: 'enumeration',
				options: getOptionsFromRes(kostenKategorie, 'Allgemein_Bezeichnung'),
			},
			P.Foerderantrag_Kostenpositions.schema.attributes.Allgemein_Bezeichnung,
		],
	},
	{
		fields: [
			{
				...P.Foerderantrag_Kostenpositions.schema.attributes.Allgemein_Geplante_Gesamtsumme,
				format: 'currency',
				disabled: true,
				validation: [
					{
						validateFunction: GetGesamtAusgabeGEGesamtEinnahme,
						params: [gesamtEinnahme, gesamtAusgabe],
						disabledValidation: false,
					},
				],
			},
		],
	},
	{
		fields: [
			{
				...P.Foerderantrag_Kostenpositions.schema.attributes.Allgemein_Kosten_Pro_Jahr,
				type: 'componentTable',
				disabledColumns: ['Zelle_1'],
				maxLevel: get(application, P.Allgemein_Projektdauerin_Jahren.path),
				fieldProps: { Zelle_2: { format: 'currency' } },
				hideDeleteButton: true,
			},
		],
		userProps: { columnToDivide: 1 },
	},
];

const mapKosten = (formStruc: FormStrucItem[], index: number) => {
	let result = formStruc.map((row) => {
		return {
			...row,
			fields: (get(row, 'fields', []) as Array<any>).map((value) => {
				let pathWithoutIndex = get(value, 'path', '') as string;
				return {
					...value,
					path:
						pathWithoutIndex.slice(0, P.Foerderantrag_Kostenpositions.path.length) +
						`.${index}` +
						pathWithoutIndex.slice(P.Foerderantrag_Kostenpositions.path.length),
				};
			}),
		};
	});
	return result;
};
export const getFormStrucEinnahmeAntragEbusse = (application: any, unternehmenId: number) => {
	let result: FormStrucItem[] = [];
	get(application, P.Foerderantrag_Kostenpositions.path, []).forEach((ele: any, i: number) => {
		if (
			get(ele, KP_P.Allgemein_Kostenposition.path) === KOSTEN_POSITION.Einnahme &&
			get(ele, KP_P.Allgemein_Kostenart_Finanzierung.path) === KOSTEN_ARTEN.Eigenmittel &&
			get(ele, `${KP_P.Unternehmen.path}.id`) === unternehmenId
		) {
			const allKostenPosition = get(application, P.Foerderantrag_Kostenpositions.path);
			const JahrDauer = get(application, P.Allgemein_Projektdauerin_Jahren.path);
			const { groupedData } = transformData(allKostenPosition, JahrDauer, get(application, P.Allgemein_Projektstart_Jahr.path));
			const gesamtEinnahme = groupedData[unternehmenId]?.gesamtEinnahme;
			const gesamtAusgabe = groupedData[unternehmenId]?.gesamtAusgabe;

			const formStrucWithoutCT = getFormStrucWithoutComponentTable(formStrucEigenmittel(application, [], gesamtEinnahme, gesamtAusgabe));
			const formStrucCTWithIndex = getFormStrucComponentTable(
				application,
				mapKosten(formStrucEigenmittel(application, [], gesamtEinnahme, gesamtAusgabe), i),
			);
			const formStrucWithIndex = mapKosten(formStrucWithoutCT, i);

			const isHinweisFieldRequired = get(ele, KP_P.Pruefung_Hinweis_Fuer_Antragsteller.path) && !isTrue(get(ele, KP_P.Pruefung_Erledigt.path));

			if (isHinweisFieldRequired) {
				result = concat(result, formStrucWithIndex, formStrucCTWithIndex, mapKosten(PruefAssistenzStruc(), i));
			} else {
				result = concat(result, formStrucWithIndex, formStrucCTWithIndex);
			}
		}
	});
	return result;
};
